<template>
  <div class="table" ref="table" v-on="tableListeners">
    <!-- 表头，存在额外列以适应因表格body出现滚动条造成的错位 -->
    <div
      v-if="!tableProps.noHeader"
      class="table-header"
      :style="{
        'grid-template-columns': colWidthHeader
      }"
      ref="header"
    >
      <div
        class="table-item table-header-item"
        v-for="(col, i) in cols"
        :key="`${col.prop}${i}`"
        :class="[
          `item-align-${col.align}`,
          `${size}`,
          headerClass(col, i),
          {
            [`item-border`]: tableProps.border,
            sortable: col.sortable
          }
        ]"
        :style="{
          'grid-column': i + 1,
          position: col.fixed || col.fixedRight ? 'sticky' : 'static',
          zIndex: col.fixed || col.fixedRight ? '998' : 'auto',
          left: col.fixed === true ? 0 : col.fixed,
          right: col.fixedRight === true ? headerFillerWidth : col.fixedRight,
          minWidth: `${col.minWidth}px`,
          ...headerStyles(col, i)
        }"
        :left-edge="i === 0"
        :right-edge="i === cols.length - 1"
      >
        <template>
          <Renderer
            class="table-header-label"
            :render="col.renderHeader"
            :props="{ column: col }"
          />

          <SjDropdownMenu
            @select="handleTableHeaderMenuSelect($event, col)"
            :menuProps="{ data: getTableHeaderSortMenu(col) }"
            :dropdownProps="{ appendToBody: true }"
            :showArrow="false"
            v-if="col.sortable"
            style="margin-left: 5px;flex-shrink: 0"
          >
            <SortIcon
              :order="getSortOrder(col)"
              :priority="getSortPriority(col)"
            />
          </SjDropdownMenu>

          <sj-dropdown
            v-if="col.filterable"
            style="margin-left: 5px;flex-shrink: 0"
            placement="bottom"
            trigger="click"
            :arrowVisible="false"
            :closeOnClick="false"
            @show="handleFilterShow(col)"
            :ref="`filterDropdown_${col.prop}`"
            customStyle
            appendToBody
          >
            <SjIcon
              :size="20"
              :iconSize="14"
              slot="reference"
              :class="[
                'filter_icon',
                { filtered: checkFiltered(filterConfig[col.prop]) }
              ]"
              :stopEvent="false"
              iconClass="comicon_filter"
            />

            <SjCard slot="dropdown" @click.stop class="filter_dropdwon">
              <Renderer
                :render="col.renderFilter"
                :props="{
                  column: col,
                  filterValue: editingFilterValue,
                  onFilterChange: val => (editingFilterValue = val)
                }"
              />
              <div class="filter_btns">
                <SjButton
                  theme="1"
                  size="mini"
                  style="width: 60px"
                  @click="confirmFilter(col)"
                >
                  确定
                </SjButton>
                <SjButton
                  theme="2"
                  size="mini"
                  style="margin-left: 8px;width: 60px"
                  @click="resetFilter(col)"
                >
                  重置
                </SjButton>
              </div>
            </SjCard>
          </sj-dropdown>
        </template>
      </div>
      <div class="table-header-filler"></div>
    </div>
    <!-- 表格主体，由于浏览器对grid存在1000*1000的限制，此处增加wrapper包裹每个最大1000*1000的表格 -->
    <div
      v-if="data && data.length"
      class="table-body-wrapper"
      ref="body"
      @scroll="onTableScroll"
      @mousewheel="mouseWheelHandler"
      @wheel="mouseWheelHandler"
      v-infinite-scroll="handleInfiniteScroll"
      infinite-scroll-immediate-check="scrollImmediateCheck"
      infinite-scroll-disabled="scrollDisabled"
      infinite-scroll-distance="scrollDistance"
    >
      <div
        class="table-body"
        :style="{
          'grid-template-columns': colWidth
        }"
        v-for="(dataPart, h) in finalData"
        :key="`data_part_${h}`"
      >
        <template v-for="(row, j) in dataPart">
          <template v-for="(column, i) in cols">
            <div
              :key="`${i}#${j}`"
              class="table-item table-body-item"
              :class="[
                {
                  [`item-align-${column.align}`]: true,
                  [`item-stripe`]: tableProps.stripe && j % 2 !== 0,
                  [`item-select`]: selection === h * 1000 + j,
                  [`item-hover`]: hover === h * 1000 + j,
                  [`item-text-hover`]: hoverTextDark === h * 1000 + j,
                  [`item-border`]:
                    tableProps.border &&
                    !(h === finalData.length - 1 && j === dataPart.length - 1),
                  [`item-ellipsis`]: column.ellipsis
                },
                `${size}`,
                cellClass(row, column, h * 1000 + j, i)
              ]"
              :style="{
                'grid-column': i + 1,
                'grid-row': j + 1,
                position:
                  column.fixed || column.fixedRight ? 'sticky' : 'static',
                zIndex: column.fixed || column.fixedRight ? '998' : 'auto',
                left: column.fixed === true ? 0 : column.fixed,
                right: column.fixedRight === true ? 0 : column.fixedRight,
                minWidth: `${column.minWidth}px`,
                ...cellStyles(row, column, h * 1000 + j, i)
              }"
              :left-edge="i === 0"
              :right-edge="i === cols.length - 1"
              @mouseenter="
                onCellOver({
                  row,
                  column,
                  columnIndex: i,
                  rowIndex: h * 1000 + j
                })
              "
              @mouseleave="
                onCellLeave({
                  row,
                  column,
                  columnIndex: i,
                  rowIndex: h * 1000 + j
                })
              "
              @click="
                onCellClick(
                  {
                    row,
                    column,
                    columnIndex: i,
                    rowIndex: h * 1000 + j
                  },
                  $event
                )
              "
            >
              <Renderer
                :class="[{ [`item-ellipsis`]: column.ellipsis }]"
                :render="column.render"
                :props="{
                  row,
                  column,
                  rowIndex: h * 1000 + j,
                  columnIndex: i
                }"
              >
              </Renderer>
            </div>
          </template>
        </template>
      </div>
    </div>
    <SjBlank v-else class="table-empty" />
    <div class="download_icon" v-if="!tableProps.noExport">
      <!--      <div class="icon_bg"></div>-->
      <!-- <i class="icon_com comtable_export" /> -->
      <SjButton
        iconClass="comtable_export"
        type="iconBtn"
        border
        @click="handleExport"
        >下载表格</SjButton
      >
    </div>
  </div>
</template>

<script>
/**
 * basetable参考Element的table复刻构造
 * 表格主体采用css grid布局
 * 支持主要element table属性
 */
import merge from "lodash.merge";
import Renderer from "./renderer";
import SortIcon from "./SortIcon";
import infiniteScroll from "vue-infinite-scroll";

export default {
  name: "SjBaseTable1",
  directives: {
    infiniteScroll
  },
  data() {
    return {
      /**
       * 保存表格的排序，过滤和选中行状态
       */
      sortConfig: [],
      filterConfig: {},

      selection: null,
      hover: null,
      hoverTextDark: null,
      headerFillerWidth: 0,

      editingFilterValue: null
    };
  },
  props: {
    columns: Array,
    data: Array,
    tableProps: Object,
    size: {
      type: String,
      default: "default",
      validator(v) {
        return ["medium", "default", "small"].indexOf(v) !== -1;
      }
    },
    // iconAsc: String,
    // iconDesc: String,
    fillValue: {
      type: String,
      default: "—"
    },
    scrollImmediateCheck: {
      type: Boolean,
      default: false
    },
    scrollDisabled: {
      type: Boolean,
      default: false
    },
    scrollDistance: {
      type: Number,
      default: 10
    }
  },
  methods: {
    checkFiltered(val) {
      if (val === null || val === undefined) return false;
      if (Array.isArray(val)) {
        if (val.every(v => v === null || v === undefined)) {
          return false;
        }
      }
      return true;
    },

    clearFilter() {
      this.filterConfig = {};
      this.editingFilterValue = null;
    },

    clearSort() {
      this.sortConfig = [];
    },

    handleFilterShow({ prop }) {
      this.editingFilterValue = this.filterConfig[prop];
    },

    confirmFilter({ prop }) {
      const dropdown = this.$refs[`filterDropdown_${prop}`]?.[0];
      if (dropdown) {
        dropdown.doClose();
      }
      this.$set(this.filterConfig, prop, this.editingFilterValue);
      this.$emit("filter-change", this.filterConfig);
    },

    resetFilter() {
      this.editingFilterValue = null;
    },

    getTableHeaderSortMenu({ sortable, prop }) {
      const menu = [];
      if (sortable) {
        const config = this.sortConfig.find(({ sortKey }) => {
          return sortKey === prop;
        });
        menu.push(
          {
            key: "sort",
            label: "排序",
            children: [
              {
                key: "ascending",
                label: "升序",
                selected: config?.sortOrder === "ascending"
              },
              {
                key: "descending",
                label: "降序",
                selected: config?.sortOrder === "descending"
              }
            ]
          },
          {
            key: "clearSort",
            label: "清除排序",
            disabled: !config
          }
        );
      }
      return menu;
    },

    getSortPriority({ prop }) {
      const index = this.sortConfig.findIndex(({ sortKey }) => {
        return sortKey === prop;
      });
      if (index >= 0) {
        return index + 1;
      } else {
        return null;
      }
    },

    getSortOrder({ prop }) {
      const config = this.sortConfig.find(({ sortKey }) => {
        return sortKey === prop;
      });
      if (config) {
        return config.sortOrder;
      } else {
        return null;
      }
    },

    onSortChange(sortOrder, { prop }) {
      const index = this.sortConfig.findIndex(({ sortKey }) => {
        return sortKey === prop;
      });

      if (sortOrder === null) {
        // clear
        if (index >= 0) {
          this.sortConfig.splice(index, 1);
        }
      } else {
        if (index >= 0) {
          this.$set(this.sortConfig[index], "sortOrder", sortOrder);
        } else {
          this.sortConfig.push({
            sortKey: prop,
            sortOrder
          });
        }
      }
      this.$emit("sort-change", this.sortConfig);
    },

    handleTableHeaderMenuSelect(key, col) {
      this.onSortChange(key === "clearSort" ? null : key, col);
    },

    handleExport() {
      // [].concat(...this.finalData)
      // console.log('click')
      // console.log(this.finalData.flat())
      //header
      this.$SjConfirm("是否要导出表格数据？")
        .then(() => {
          const handleText = innerValue => {
            let result = innerValue.replace(/"/g, '""');
            if (result.search(/("|,|\n)/g) >= 0) result = '"' + result + '"';
            return result;
          };
          const table = [];
          if (this.$refs.header) {
            const headers = Array.from(
              this.$refs.header.querySelectorAll(".table-header-item")
            )
              .filter((node, index) => {
                return this.cols[index].exportable;
              })
              .map(node => {
                return handleText(node.innerText);
              });
            table.push(headers.join(","));
            console.log(headers);
          }
          if (this.$refs.body) {
            let arr = [];
            Array.from(
              this.$refs.body.querySelectorAll(".table-body-item")
            ).forEach((node, index) => {
              if (this.cols[index % this.cols.length].exportable) {
                const row = this.data[parseInt(index / this.cols.length)];
                const text = this.cols[index % this.cols.length].exportValue
                  ? this.cols[index % this.cols.length].exportValue(row)
                  : node.innerText;
                arr.push(handleText(text ? text : ""));
              }
              if ((index + 1) % this.cols.length === 0) {
                table.push(arr.join(","));
                arr = [];
              }
            });
          }
          const csv = table.join("\n");
          const blob = new Blob(["\uFEFF" + csv], {
            type: "text/csv;charset=utf-8;"
          });
          const href = URL.createObjectURL(blob);
          this.$SjDownload({
            href,
            download: "导出表格.csv"
          });
        })
        .catch(() => {});
    },

    /**
     * 辅助方法，按照一定大小切割数组
     * 用于将data分割为1000大小的段以解决grid限制
     * @param {array} data - 待分割数据
     * @param {number} chunkSize - 分割块大小
     */
    splitChunk(data, chunkSize = 1000) {
      let start = 0;
      let end = data.length;
      let res = [];
      for (; start < end; start += chunkSize) {
        res.push(data.slice(start, start + chunkSize));
      }
      return res;
    },
    /**
     * 辅助方法，根据当前排序方法获取下一种排序法
     * @param {string} v - 当前排序方法
     */
    sortNext(v) {
      switch (v) {
        case undefined:
          return "ascending";
        case "ascending":
          return "descending";
        case "descending":
          return undefined;
        default:
          return undefined;
      }
    },

    /**
     * 滚动高亮当前行背景
     */
    onCellOver({ row, column, rowIndex, columnIndex }) {
      if (
        this.tableProps.highlightHover ||
        this.tableProps["highlight-hover"]
      ) {
        this.hover = rowIndex;
      }
      if (this.tableProps.darkTextHover || this.tableProps["dark-text-hover"]) {
        this.hoverTextDark = rowIndex;
      }
      this.$emit("mouse-enter", {
        row,
        column,
        rowIndex,
        columnIndex
      });
    },
    onCellLeave({ row, column, rowIndex, columnIndex }) {
      this.hover = null;
      this.$emit("mouse-leave", {
        row,
        column,
        rowIndex,
        columnIndex
      });
    },

    /**
     * 表格单元格点击事件，上报事件
     */
    onCellClick({ row, column, rowIndex, columnIndex }, e) {
      if (
        this.tableProps.highlightCurrentRow ||
        this.tableProps["highlight-current-row"]
      ) {
        this.$emit(
          "current-change",
          this.data[rowIndex],
          this.data[this.selection]
        );
        this.selection = rowIndex;
      }
      this.$emit(
        "cell-click",
        {
          row,
          column,
          rowIndex,
          columnIndex
        },
        e
      );
    },
    /**
     * 表格行点击事件，上报事件
     */
    setCurrentRow(row) {
      const rowIndex = this.data.indexOf(row);
      if (rowIndex === -1) return;
      if (this.selection === rowIndex) {
        this.selection === null;
      } else {
        this.$emit(
          "current-change",
          this.data[rowIndex],
          this.data[this.selection]
        );
        this.selection = rowIndex;
      }
    },
    /**
     * 使表头和body同步滚动
     */
    onTableScroll() {
      if (!this.tableProps.noHeader) {
        this.$refs.header.scrollLeft = this.$refs.body.scrollLeft;
      }
    },
    handleInfiniteScroll() {
      this.$emit("infiniteScroll");
    },
    mouseWheelHandler(e) {
      e.stopPropagation();
    },
    // clearSort() {
    //   this.sortConfig = [];
    // },
    // clearFilter() {
    //   this.filterConfig = {};
    //   this.$refs.filter &&
    //     this.$refs.filter.forEach(component => {
    //       component.clearFilter();
    //     });
    // },
    /**
     * 为表头和表格单元格自定义class与样式
     */
    headerClass(column, columnIndex) {
      const res = {};
      if (this.tableProps.headerClassName) {
        if (this.tableProps.headerClassName instanceof Function) {
          res[this.tableProps.headerClassName({ column, columnIndex })] = true;
        } else {
          res[this.tableProps.headerClassName] = true;
        }
      }
      return res;
    },
    headerStyles(column, columnIndex) {
      let res = {};
      if (this.tableProps.headerStyles) {
        if (this.tableProps.headerStyles instanceof Function) {
          res = this.tableProps.headerStyles({ column, columnIndex });
        } else {
          res = this.tableProps.headerStyles;
        }
      }
      return res;
    },
    cellClass(row, column, rowIndex, columnIndex) {
      const res = {};
      if (this.tableProps.rowClassName) {
        if (this.tableProps.rowClassName instanceof Function) {
          res[this.tableProps.rowClassName({ row, rowIndex })] = true;
        } else {
          res[this.tableProps.rowClassName] = true;
        }
      }
      if (this.tableProps.cellClassName) {
        if (this.tableProps.cellClassName instanceof Function) {
          res[
            this.tableProps.cellClassName({
              row,
              column,
              rowIndex,
              columnIndex
            })
          ] = true;
        } else {
          res[this.tableProps.cellClassName] = true;
        }
      }
      return res;
    },
    cellStyles(row, column, rowIndex, columnIndex) {
      let res = {};
      if (this.tableProps.rowStyles) {
        if (this.tableProps.rowStyles instanceof Function) {
          res = this.tableProps.rowStyles({ row, rowIndex });
        } else {
          res = this.tableProps.rowStyles;
        }
      }
      if (this.tableProps.cellStyles) {
        if (this.tableProps.cellStyles instanceof Function) {
          res = this.tableProps.cellStyles({
            row,
            column,
            rowIndex,
            columnIndex
          });
        } else {
          res = this.tableProps.cellStyles;
        }
      }
      return res;
    },
    // onFilterChange(prop, filter) {
    //   if (!filter) {
    //     this.filterConfig = {
    //       ...this.filterConfig,
    //       [prop]: null
    //     };
    //   } else {
    //     this.filterConfig = {
    //       ...this.filterConfig,
    //       [prop]: {
    //         key: filter,
    //         method: this.cols.find(v => v.prop === prop).customFilter
    //       }
    //     };
    //   }
    //   this.$emit("filter-change", this.filterConfig);
    // },
    /**
     * column预处理
     * 为column注入
     * formatter和表头和表格的renderer
     */
    colFormation(columns) {
      return columns.map(
        col => ({
          align: "left",
          formatter: function(data) {
            return data;
          },

          renderHeader: (h, { column }) => <div>{column.label}</div>,
          customFilter: (key, data) => {
            if (Array.isArray(key)) {
              if (!key.length) return true;
              return (
                key.findIndex(sub => {
                  return String(data).includes(sub);
                }) !== -1
              );
            }
            return String(data).includes(key);
          },
          exportable: true,
          ...col,
          render:
            col.customRender ||
            ((h, { row, column }) => (
              <div>
                {this.fillFn(
                  column.formatter
                    ? column.formatter(row[column.prop])
                    : row[column.prop]
                )}
              </div>
            ))
        })
        // }
      );
    },
    /**
     * 填充默认值
     */
    fillFn(val) {
      const { tableProps, fillValue } = this;
      if (!tableProps.cancelFill && !val && val !== 0) {
        return fillValue;
      }
      return val;
    },
    /**
     * 初始化表格，根据配置指定样式
     */
    tableInit() {
      // height
      const height = this.tableProps.height;
      if (height) {
        if (typeof height === "number") {
          this.$el.style.height = `${height}px`;
        }
        if (typeof height === "string") {
          this.$el.style.height = height;
        }
      }
      // max-height
      const maxHeight =
        this.tableProps.maxHeight || this.tableProps["max-height"];
      if (maxHeight) {
        if (typeof maxHeight === "number") {
          this.$el.style.maxHeight = `${maxHeight}px`;
        }
        if (typeof maxHeight === "string") {
          this.$el.style.maxHeight = maxHeight;
        }
      }
    },
    getHeaderFillerWidth() {
      if (process.env.VUE_ENV !== "server") {
        if (
          !this.tableProps.noHeader &&
          this.data?.length &&
          this.$el.scrollHeight <
            this.$el.querySelector(".table-header").scrollHeight +
              this.$el.querySelector(".table-body").scrollHeight
        ) {
          const outer = document.createElement("div");
          outer.style.width = "100vw";
          outer.style.overflow = "scroll";

          const inner = document.createElement("div");
          inner.style.width = "100%";

          outer.appendChild(inner);
          document.body.appendChild(outer);
          const scrollBarWidth = outer.offsetWidth - inner.offsetWidth;

          inner.parentNode.removeChild(inner);
          outer.parentNode.removeChild(outer);

          return `${scrollBarWidth}px`;
        } else {
          return 0;
        }
      }
    },
    initCurrentChange(newData, oldData) {
      if (newData?.length) {
        if (this.selection === null) {
          // init selection
          if (
            Object.prototype.hasOwnProperty.call(
              this.tableProps,
              "defaultSelection"
            )
          ) {
            this.selection = this.tableProps.defaultSelection;
          }
        }
        if (this.selection !== null) {
          if (newData.length > this.selection) {
            this.$emit(
              "current-change",
              newData[this.selection],
              oldData && oldData[this.selection]
            );
          }
        }
      }
    },
    selectItemToView(className) {
      this.$nextTick(() => {
        let selectItem = document.getElementsByClassName(className)[0];
        selectItem.scrollIntoView({ block: "end" });
      });
    }
  },
  computed: {
    /**
     * 输出经过排序和过滤，并每1000条切割的的最终data
     */
    finalData() {
      const { data } = this;
      if (!data) return [];
      const copy = merge([], data);
      // if (!this.$listeners["sort-change"] && !this.$listeners.sortChange) {
      //   if (this.sortConfig.sortOrder) {
      //     copy.sort(
      //       this.sortConfig.sortMethod.bind(null, this.sortConfig.sortOrder)
      //     );
      //   }
      // }

      return this.splitChunk(copy, 1000);
    },
    /**
     * 后处理的columns，对一些属性增加默认值
     */
    cols() {
      const { columns } = this;
      if (!columns) return null;
      return this.colFormation(columns);
    },
    /**
     * 对使用width的列配置宽度
     * 对使用min-width的列配置缩放比
     */
    colWidth() {
      return this.cols
        .map(v => {
          if (v.width) {
            return `${v.width}px`;
          }
          if (v.minWidth) {
            return `${v.minWidth}fr`;
          }
          return "80fr";
        })
        .join(" ");
    },
    /**
     * 表头的宽度配置，增加了滚动条filler
     */
    colWidthHeader() {
      return `${this.colWidth} ${this.headerFillerWidth}`;
    },
    /**
     * 绑定table事件
     */
    tableListeners() {
      return Object.assign({}, this.$listeners);
    }
  },
  watch: {
    // filterConfig:{
    //   deep: true,
    //   handler(){
    //     console.log('filterConfig chagne');
    //   }
    // },
    data: {
      immediate: true,
      /**
       * 在数据数量发生变化时重新判断是否需要滚动条filler
       */
      handler(val, oldVal) {
        this.$nextTick(() => {
          this.headerFillerWidth = this.getHeaderFillerWidth();
          this.initCurrentChange(val, oldVal);
        });
      }
    },
    tableProps() {
      this.tableInit();
    }
  },
  mounted() {
    this.tableInit();
  },
  components: {
    Renderer,
    SortIcon
  }
};
</script>

<style lang="less" scoped>
.icon-color {
  color: @color-normal;
}
.table {
  /*padding: 5px;*/
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  position: relative;
  &:hover {
    /*overflow: hidden;*/
    .download_icon {
      visibility: visible;
    }
  }
  .download_icon {
    cursor: pointer;
    visibility: hidden;
    position: absolute;
    z-index: 1000;
    top: -30px;
    right: 0;
    background-color: @color-gray-3;
    display: flex;
    align-items: center;
    justify-content: center;
    &:hover,
    &:active {
      i {
        color: @color-highlight;
      }
    }
    .icon_bg {
      position: absolute;
      top: 0;
      right: 0;
      width: 0;
      height: 0;
      border-style: solid;
      border-width: 0 30px 30px 0;
      border-color: transparent @color-gray-3 transparent transparent;
    }
    i {
      color: @color-available;
      font-size: 14px;
    }
  }
}
.table-header {
  flex-shrink: 0;
  display: grid;
  overflow: hidden;
}
.table-body-wrapper {
  flex: 1;
  min-height: 0;
  overflow: auto;
}
.table-body {
  display: grid;
  grid-auto-flow: column;
  min-width: 100%;
  width: min-content;
}
.table-item {
  display: inline-flex;
  align-items: center;
  padding: 10px;
  box-sizing: border-box;
  transition: background-color 0.2s;
  background-color: @table-item-background;
  .item-show {
    display: block !important;
  }
  .sort_center_box {
    flex: 1;
    text-align: center;
  }

  .item-ellipsis {
    .text-ellipsis();
  }
  &.item-ellipsis {
    .text-ellipsis();
  }
  &.item-border {
    border-bottom: 1px solid @table-border-color;
  }
  &.small {
    .text("caption", "dark");
    padding: 5px 10px;
  }
  &.default {
    .text("caption", "dark");
  }
  &.medium {
    .text("body", "dark");
  }
  &.item-align-left {
    justify-content: flex-start;
  }
  &.item-align-center {
    justify-content: center;
  }
  &.item-align-right {
    justify-content: flex-end;
  }
  &.item-stripe {
    background-color: @table-item-stripe-background;
  }

  &.item-select {
    background-color: @table-item-highlight-background;
    .font_color("highlight");
  }
  &.item-hover {
    background-color: @table-item-highlight-background;
    .font_color("highlight");
  }
  &.item-text-hover {
    .font_color("dark");
  }
  &.table-header-item {
    .font_color("subinfo");
    word-break: break-word;
    background-color: @table-header-item-background;
    display: flex;
    align-items: center;
    &.sortable {
      cursor: pointer;
    }
    .filter_icon {
      &.filtered {
        /deep/ i {
          color: @color-highlight !important;
        }
      }
    }

    .table-header-label {
      display: inline-block;
      flex-grow: 1;
      min-width: 0;
    }
  }
  &.table-body-item {
    word-break: break-word;
  }
  &[left-edge] {
    padding-left: 5px;
  }
  &[right-edge] {
    padding-right: 5px;
  }
}

.table-empty {
  min-height: 100px;
}

.filter_dropdwon {
  width: 140px;
  padding: 10px 5px;
  box-sizing: border-box;
  .filter_btns {
    margin-top: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
